import axios from 'axios';
import {
  isTokenMetaValid,
  removeTokensMetadata,
  setTokenMetadataInLocalStorage,
} from 'src/utils/jwt';

interface accessPayload {
  data: {
    access: string;
  };
}

let lastTokenUpdate = 0;
let refreshPromise = null;

const THIRTY_SECONDS = 30 * 1000;

const shouldUpdateToken = (): boolean => {
  return lastTokenUpdate + THIRTY_SECONDS < Date.now();
};

export const refreshAccessToken = (): Promise<unknown> => {
  lastTokenUpdate = Date.now();
  const promise = axios.post('token/access/', null, {
    baseURL: import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/',
    withCredentials: true,
  });

  refreshPromise = promise
    .then((result: accessPayload) => {
      setTokenMetadataInLocalStorage(result.data.access, 'accessTokenMetadata');
    })
    .catch((error: { response?: { status?: number } }) => {
      // A 401 means the refresh token itself is expired/invalid — the session
      // is over and tokens must be cleared. Transient failures (network, 5xx)
      // must NOT clear tokens or hard-redirect to the login page; the caller
      // decides how to recover.
      if (error?.response?.status === 401) {
        removeTokensMetadata();
      }
      return Promise.reject(error);
    });

  return refreshPromise;
};

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_ROOT || 'http://localhost:8000/api/',
  withCredentials: true,
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      (error?.response?.status === 401 || error?.response?.status === 400) &&
      error?.response?.config?.url === 'token/access/'
    ) {
      sessionStorage.clear();
      removeTokensMetadata();
    }

    if (
      error?.response?.status === 401 &&
      error?.response?.config?.url !== 'token/both/?validate' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      try {
        // Redundancy system, in case the first filter fails to update the token
        if (shouldUpdateToken()) {
          refreshAccessToken();
        }
        await refreshPromise;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    let newError = error.message || 'Something went wrong';

    if (error.response && error.response.data) {
      newError = { ...error.response.data, status: error?.response?.status };
    }

    return Promise.reject(newError);
  }
);

axiosInstance.interceptors.request.use(
  async function (config) {
    // Start session whenever a request is called
    const accessMetadata = window.localStorage.getItem('accessTokenMetadata');
    const accessInvalid =
      accessMetadata && !isTokenMetaValid('accessTokenMetadata');
    if (accessInvalid && shouldUpdateToken()) {
      refreshAccessToken();
    }
    if (refreshPromise) {
      await refreshPromise;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

export default axiosInstance;
